import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { trpc } from "../../main/components/trpc.ts";
import { downloadPayslipPdf } from "../downloadPayslipPdf.ts";

export default function SavedPayslipsPanel({
  secretaryId,
  enabled,
}: {
  secretaryId?: string;
  enabled: boolean;
}) {
  const utils = trpc.useUtils();
  const saved = trpc.payroll.listSavedPayslips.useInfiniteQuery(
    { secretaryId: secretaryId || undefined, limit: 20 },
    { enabled, getNextPageParam: (page) => page.nextCursor ?? undefined },
  );
  const download = trpc.payroll.downloadSavedPayslipPdf.useMutation({
    onSuccess: async () => {
      await utils.payroll.listSavedPayslips.invalidate();
    },
  });
  async function getPdf(id: string) {
    try {
      downloadPayslipPdf(await download.mutateAsync({ id }));
    } catch {
      toast.error("Impossible de télécharger cette fiche enregistrée.");
    }
  }
  const items = saved.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <section className="mt-6 rounded-[18px] border border-slate-200 bg-white p-5">
      <h2 className="text-lg font-semibold">Fiches de paie enregistrées</h2>
      <p className="mt-1 text-sm text-slate-500">
        Chaque version est figée et disponible dans T Extension, même sans
        téléchargement du PDF.
      </p>
      {saved.isLoading && <p role="status">Chargement…</p>}
      {saved.error && (
        <p role="alert" className="text-red-700">
          Impossible de charger les fiches.{" "}
          <button type="button" onClick={() => void saved.refetch()}>
            Réessayer
          </button>
        </p>
      )}
      {!saved.isLoading && !saved.error && !items.length && (
        <p className="mt-3 text-sm">
          {secretaryId
            ? "Aucune fiche enregistrée pour ce salarié."
            : "Aucune fiche enregistrée."}
        </p>
      )}
      <ul className="mt-3 space-y-2">
        {items.map((payroll) => (
          <li
            key={payroll.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 p-3"
          >
            <div>
              <p className="font-medium">
                {payroll.employee.fullname} —{" "}
                {DateTime.fromISO(payroll.periodStart)
                  .setLocale("fr")
                  .toFormat("LLLL yyyy")}{" "}
                · version {payroll.revision}
              </p>
              <p className="text-sm text-slate-500">
                Enregistrée le{" "}
                {DateTime.fromISO(payroll.generatedAt).toFormat(
                  "dd/MM/yyyy à HH:mm",
                )}{" "}
                · {payroll.remuneration.replace(".", ",")} €{" "}
                {payroll.salaryType === "net" ? "net" : "brut"} hors prime
              </p>
            </div>
            <button
              type="button"
              disabled={download.isPending}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              onClick={() => void getPdf(payroll.id)}
            >
              {download.isPending && download.variables?.id === payroll.id
                ? "Préparation…"
                : "Télécharger le PDF"}
            </button>
          </li>
        ))}
      </ul>
      {saved.hasNextPage && (
        <button
          type="button"
          disabled={saved.isFetchingNextPage}
          className="mt-3 text-sm text-blue-700"
          onClick={() => void saved.fetchNextPage()}
        >
          Fiches précédentes
        </button>
      )}
    </section>
  );
}
